import {ContextMenuPrimitive as ContextMenu} from "ch3-ui-lib";
import {Clock, Moon, MoonStar, Settings} from "lucide-react";
import type {ReactNode} from "react";
import {silencedUntil} from "./dndTime.ts";

const icon = '!p-0 shrink-0 !text-current';

export interface SilencedLine {
    label: string;
    dueDate: Date | null;
}

interface DndStatusBannerProps {
    /** Global DND wins: when it is on, every line is silenced. */
    global: { enabled: boolean; dueDate: Date | null };
    lines: SilencedLine[];
    onTurnOff: () => void;
}

/** Dnd-Calls: status block in the lines column while any DND is on. */
export const DndStatusBanner = ({global, lines, onTurnOff}: DndStatusBannerProps) => {
    if (!global.enabled && lines.length === 0) return null;

    const detail = global.enabled
        ? `All calls silenced ${silencedUntil(global.dueDate)}`
        : lines.length === 1
            ? `${lines[0].label} silenced ${silencedUntil(lines[0].dueDate)}`
            : `${lines.length} lines silenced`;

    return (
        <div role="status" className="mt-2 flex flex-col gap-1 rounded-control bg-brand-tint p-2.5 text-footnote font-medium leading-[18px] text-brand-ink">
            <span className="flex items-center gap-1.5 font-semibold">
                <Moon size={14} aria-hidden="true" className={icon}/>
                Do not disturb is on
            </span>
            <span>{detail}</span>
            <button type="button" onClick={onTurnOff} className="w-fit cursor-pointer font-semibold text-brand-ink underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus">
                Turn off
            </button>
        </div>
    );
};

interface LineContextMenuProps {
    children: ReactNode;
    dndOn: boolean;
    onDnd: () => void;
    onTurnOff: () => void;
    onAdjust: () => void;
    onSettings: () => void;
}

const itemClass = 'flex min-h-9 cursor-pointer select-none items-center gap-2.5 rounded-chip px-2.5 text-callout text-white outline-none data-[highlighted]:bg-zinc-800';

/** Dnd-LineMenu: right-click (or the context-menu key) on a line in the Calls column. */
export const LineContextMenu = ({children, dndOn, onDnd, onTurnOff, onAdjust, onSettings}: LineContextMenuProps) => (
    <ContextMenu.Root>
        <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
        <ContextMenu.Portal>
            <ContextMenu.Content className="z-50 w-60 rounded-xl bg-zinc-950 p-1 shadow-menu">
                {dndOn ? (
                    <>
                        <ContextMenu.Item onSelect={onTurnOff} className={`${itemClass} !text-menu-danger`}>
                            <Moon size={16} aria-hidden="true" className={icon}/>Turn off Do not disturb
                        </ContextMenu.Item>
                        <ContextMenu.Item onSelect={onAdjust} className={itemClass}>
                            <Clock size={16} aria-hidden="true" className={`${icon} !text-zinc-300`}/>Adjust time
                        </ContextMenu.Item>
                    </>
                ) : (
                    <ContextMenu.Item onSelect={onDnd} className={itemClass}>
                        <MoonStar size={16} aria-hidden="true" className={`${icon} !text-zinc-300`}/>Do not disturb…
                    </ContextMenu.Item>
                )}
                <ContextMenu.Item onSelect={onSettings} className={itemClass}>
                    <Settings size={16} aria-hidden="true" className={`${icon} !text-zinc-300`}/>Line settings
                </ContextMenu.Item>
            </ContextMenu.Content>
        </ContextMenu.Portal>
    </ContextMenu.Root>
);
